/**
 * NotificationContainer Component
 * Handles positioning and layout of notification items
 */

import React from 'react'

import { BaseComponentProps } from '@/shared/types/common.types'

import { NotificationItem } from './NotificationItem'
import type { AppNotification } from './NotificationProvider'

interface NotificationContainerProps extends BaseComponentProps {
  notifications: AppNotification[]
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  onDismiss: (id: string) => void
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  position,
  onDismiss,
  testId = 'notification-container',
}) => {
  if (notifications.length === 0) return null

  const getPositionClasses = () => {
    const baseClasses = 'fixed z-[9999] pointer-events-none'

    switch (position) {
      case 'top-left':
        return `${baseClasses} top-4 left-4`
      case 'top-right':
        return `${baseClasses} top-4 right-4`
      case 'bottom-left':
        return `${baseClasses} bottom-4 left-4`
      case 'bottom-right':
      default:
        return `${baseClasses} bottom-4 right-4`
    }
  }

  const isBottom = position.includes('bottom')

  return (
    <>
      {/* Aria-live region for screen readers to announce notifications */}
      <div aria-live="polite" aria-atomic="false" aria-relevant="additions" className="sr-only">
        {notifications.map((notification) => (
          <span key={notification.id}>
            {notification.title ? `${notification.title}: ` : ''}
            {notification.message}
          </span>
        ))}
      </div>

      <div className={getPositionClasses()} data-testid={testId}>
        <div className={`flex flex-col gap-3 w-80 ${isBottom ? 'flex-col-reverse' : ''}`}>
          {notifications.map((notification, index) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onDismiss={onDismiss}
              index={index}
              isFromBottom={isBottom}
            />
          ))}
        </div>
      </div>
    </>
  )
}
